import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoBlack from "@/assets/logo-black.png";
import logoWhite from "@/assets/logo-white.png";

const navItems = [
  { label: "About", path: "/about" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "Services", path: "/services" },
  { label: "Calculator", path: "/calculator" },
  { label: "Media", path: "/media" },
  { label: "Blog", path: "https://blog.avdesignintl.com" },
  { label: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isHomeTop = (location.pathname === "/" || location.pathname === "/about") && !scrolled;
  const navTextColor = isHomeTop ? "text-white" : "text-foreground";

  const navBorderColor = isHomeTop
    ? "border-white text-white hover:bg-white hover:text-background"
    : "border-foreground text-foreground hover:bg-foreground hover:text-background";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isExternal = (path: string) => path.startsWith("http");

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-foreground focus:text-background focus:px-4 focus:py-2 focus:text-sm"
      >
        Skip to main content
      </a>

      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        aria-label="Primary"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/95 backdrop-blur-sm border-b border-border"
            : "bg-transparent"
        }`}
      >
        <div className="flex items-center justify-between px-6 md:px-12 lg:px-24 py-5">
          <Link to="/" className="z-50 flex items-center" aria-label="AV Design International — Home">
            <img
              src={isHomeTop ? logoWhite : logoBlack}
              alt="AV Design International"
              width={180}
              height={64}
              className={`w-auto transition-all duration-500 ease-out ${
                scrolled ? "h-10 md:h-12" : "h-16 md:h-20"
              }`}
              style={{
                filter: isHomeTop
                  ? "drop-shadow(0 2px 12px rgba(0,0,0,0.45))"
                  : "drop-shadow(0 1px 2px rgba(0,0,0,0.08))",
              }}
              fetchPriority="high"
            />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) =>
              isExternal(item.path) ? (
                <a
                  key={item.path}
                  href={item.path}
                  className={`text-sm font-body font-light tracking-[0.2em] uppercase hover:text-accent transition-colors duration-300 ${navTextColor}`}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-body font-light tracking-[0.2em] uppercase hover:text-accent transition-colors duration-300 ${navTextColor}`}
                >
                  {item.label}
                </Link>
              )
            )}

            <Link
              to="/contact"
              className={`text-sm font-body font-light tracking-[0.2em] uppercase border px-6 py-2.5 transition-all duration-300 ${navBorderColor}`}
            >
              Start a Project
            </Link>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden z-50 transition-colors duration-300 ${navTextColor} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? (
              <X size={24} aria-hidden="true" />
            ) : (
              <Menu size={24} aria-hidden="true" />
            )}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            className="fixed inset-0 z-40 bg-background flex flex-col items-center justify-center gap-8"
          >
            {navItems.map((item, i) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.2 }}
              >
                {isExternal(item.path) ? (
                  <a
                    href={item.path}
                    className="heading-lg text-foreground hover:text-accent transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    className="heading-lg text-foreground hover:text-accent transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Link
                to="/contact"
                className="mt-4 text-sm font-body font-light tracking-[0.2em] uppercase border border-foreground px-8 py-3 text-foreground hover:bg-foreground hover:text-background transition-all duration-300"
              >
                Start a Project
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
